import { useState } from 'react'

import { Box, Button, Divider, Radio } from '@factorialco/deprecated-design-system'
import { LedgerAccountsDataTableDocument } from 'generated/resources/datatable.graphql'
import {
  CreateLedgerAccountDocument,
  LedgerAccountsImportModalTemplateDocument,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useLazyQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { downloadBlob } from 'modules/core/lib/download'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import {
  LedgerAccountCreationForm,
  LedgerAccountFormValues,
  formValidation,
} from 'modules/finance/components/Accounting/NewLedgerAccountForm'
import {
  BulkImportForm,
  LedgerAccountBulkImportFormValues,
} from 'modules/finance/components/Accounting/NewLedgerAccountImportForm'
import { useAccountsImport } from 'modules/finance/hooks/accounting/useAccountsImport'
import type { LedgerAccountsLegalEntity } from 'modules/finance/pages/Accounting/AccountsPage'

export interface AddNewAccountsLedgerAccountCTAProps {
  legalEntities: LedgerAccountsLegalEntity[]
  selectedLegalEntityId: string
  onClose: () => void
}

export const AddNewAccountsLedgerAccountCTA = ({
  onClose,
  legalEntities,
  selectedLegalEntityId,
}: AddNewAccountsLedgerAccountCTAProps) => {
  const [mode, setMode] = useState<'one_account' | 'bulk_import' | ''>('')
  const [createLedgerAccount] = useMutation(CreateLedgerAccountDocument, {
    extractorFn: (dataP) => dataP.financeMutations.createFinanceAccount,
    refetchQueries: [LedgerAccountsDataTableDocument],
  })
  const {
    parseImportFile,
    submitImportFile,
    parseSummary,
    validateParseErrors,
    validateSubmitErrors,
    parsing,
    submitting,
  } = useAccountsImport()

  const creationForm = useForm<LedgerAccountFormValues>({
    defaultValues: {
      number: '',
      name: '',
      isDisabled: false,
      externalId: '',
      type: 'bank',
      legalEntityId: selectedLegalEntityId,
    },
    validateOnBlur: true,
    validate: formValidation,
    onSubmit: async (values) => {
      const legalEntity = legalEntities.find((el) => el.id === values.legalEntityId)
      if (!legalEntity) {
        return {
          errors: [
            {
              type: 'SimpleError',
              field: 'legalEntityId',
              message: i18n.t('finance.account_ledger.new_modal.generic_error'),
            },
          ],
          success: false,
        }
      }
      const result = await createLedgerAccount({
        variables: {
          currency: legalEntity.currency,
          externalId: values.externalId,
          legalEntityId: legalEntity.id,
          name: values.name,
          number: values.number,
          type: values.type,
        },
      })
      const handled = handleResult(result, (errors) => {
        creationForm.setErrors(errors)
      })
      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('finance.account_ledger.new_modal.account_created'),
        })
        creationForm.resetForm()
        onClose()
      } else {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('errors.generic_error'),
        })
      }
      return handled
    },
  })

  const importBulkForm = useForm<LedgerAccountBulkImportFormValues>({
    defaultValues: { prefillToggleValue: false, legalEntityId: selectedLegalEntityId },
    validateOnChange: true,
    validate: importBulkFormValidation,
    onSubmit: async (values) => {
      if (values.file === undefined) {
        return {
          errors: [
            {
              type: 'SimpleError',
              field: 'file',
              message: i18n.t('finance.account_ledger.new_modal.generic_error'),
            },
          ],
          success: false,
        }
      }

      const result = await submitImportFile(values.legalEntityId, values.file)

      const handled = handleResult(result, () => null)

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
        importBulkForm.resetForm()
        onClose()
      }

      return handled
    },
  })

  const [fetchTemplate] = useLazyQuery(LedgerAccountsImportModalTemplateDocument, {
    variables: {
      ...(importBulkForm.values.prefillToggleValue && { prefill: 'current_data' }),
      legalEntityId: importBulkForm.values.legalEntityId,
    },
  })

  const onDownloadTemplate = async () => {
    const { data: templateResponse } = await fetchTemplate()
    const template = templateResponse?.finance.accountImportTemplatesConnection?.edges?.[0]?.node
    if (!template) return

    const base64File = template.fileInBase64
    const fileResponse = await fetch(
      `data:${template.fileType};base64,${base64File.replace(/^data:.*,/, '')}`
    )

    const blob = await fileResponse.blob()

    downloadBlob(blob, template.fileName)
  }

  const onDropFile = (files: File[]) => {
    const droppedFile = files[0]
    importBulkForm.setFieldValue('file', droppedFile)
    if (droppedFile) {
      parseImportFile(importBulkForm.values.legalEntityId, droppedFile)
    }
  }

  const handleSubmit = async () => {
    if (mode === 'one_account') {
      await creationForm.submitForm()
    } else if (mode === 'bulk_import') {
      await importBulkForm.submitForm()
    }
    track(trackingEvents.accounting.accounting.clickSaveAddImportAccount, { mode })
  }

  return (
    <Modal
      title={i18n.t('finance.chart_of_accounts.add_new_accounts_modal.title')}
      description={i18n.t('finance.chart_of_accounts.add_new_accounts_modal.description')}
      illustration={'add-block'}
      onClose={onClose}
      footer={
        <Box flexDirection="row" justifyContent="flexEnd" width="full" gap={'s8'}>
          <Button onClick={onClose} type="secondary">
            {i18n.t('common.cancel')}
          </Button>
          <Button onClick={handleSubmit} type="primary">
            {i18n.t('common.save')}
          </Button>
        </Box>
      }
    >
      <Box gap="s16" flexDirection="column">
        <Box gap="s8" flexDirection="column">
          <Radio
            name="visibility"
            description={i18n.t(
              'finance.chart_of_accounts.add_new_accounts_modal.option_one_account.description'
            )}
            option={{
              label: i18n.t(
                'finance.chart_of_accounts.add_new_accounts_modal.option_one_account.title'
              ),
              value: 'one_account' as const,
              disabled: false,
            }}
            value={mode ?? ''}
            onChange={(value) => setMode(toSelectType(value))}
          />
          <Radio
            name="visibility"
            description={i18n.t(
              'finance.chart_of_accounts.add_new_accounts_modal.option_bulk_import.description'
            )}
            option={{
              label: i18n.t(
                'finance.chart_of_accounts.add_new_accounts_modal.option_bulk_import.title'
              ),
              value: 'bulk_import' as const,
              disabled: false,
            }}
            value={mode}
            onChange={(value) => {
              if (value === 'bulk_import') {
                track(trackingEvents.accounting.accounting.bulkImportAccounts)
              }
              setMode(toSelectType(value))
            }}
          />
        </Box>
        {mode !== '' && <Divider />}
        {mode === 'one_account' && (
          <LedgerAccountCreationForm form={creationForm} legalEntities={legalEntities} />
        )}
        {mode === 'bulk_import' && (
          <BulkImportForm
            form={importBulkForm}
            onDropFile={onDropFile}
            onDownloadTemplate={onDownloadTemplate}
            legalEntities={legalEntities}
            loading={parsing || submitting}
            loadingMessage={
              parsing
                ? i18n.t('finance.accounting_accounts.import_accounts_modal.checking_file_format')
                : i18n.t('finance.accounting_accounts.import_accounts_modal.spinner_message')
            }
            summary={parseSummary}
            errors={validateSubmitErrors || validateParseErrors}
          />
        )}
      </Box>
    </Modal>
  )
}

const importBulkFormValidation = (values: LedgerAccountBulkImportFormValues) => {
  const errors: Record<string, string> = {}
  if (!values.legalEntityId) {
    errors.legalEntityId = i18n.t('finance.account_ledger.new_modal.validation_mandatory')
  }
  if (!values.file) {
    errors.file = i18n.t('finance.account_ledger.new_modal.validation_mandatory')
  }
  return errors
}

const toSelectType = (value: string) => {
  if (value === 'one_account') return 'one_account'
  if (value === 'bulk_import') return 'bulk_import'
  return ''
}
